import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import AssetPlayer from "./AssetPlayer";
import { useContentPlayer } from "./hooks/ContentPlayerProvider";
import { useVideoPlayerRef } from "./hooks/useVideoPlayerRef";
import { Playlist, PlaylistItem } from "./imports";
import LayoutPlayer from "./LayoutPlayer";
import { groupBy } from "./utils";
import isPlaylistItemActive from "./utils/isPlaylistItemActive";
import VideoPlayer from "./VideoPlayer";
import { useLayouts } from "@hooks/useLayouts";
import { usePublicTemplates } from "@hooks/usePublicTemplates";
import { useTemplates } from "@hooks/useTemplates";

export const luxonDateFormat = "yyyy-LL-dd, HH:mm";

export default function PlaylistPlayer({
  playlist: fetchedPlaylist,
}: {
  playlist: Playlist;
}) {
  const { layouts: _layouts } = useLayouts()
  const { templates } = useTemplates()
  const { templates: publicTemplates } = usePublicTemplates()
  const layouts = [..._layouts, ...publicTemplates, ...templates]
  const { loading } = useContentPlayer()

  const [currentFrame, setCurrentFrame] = useState<PlaylistItem | null>(null);

  useEffect(() => {
    initializePlaylist(fetchedPlaylist?.items ?? [])
  }, [fetchedPlaylist]);

  const { onVideoEndedRef } = useVideoPlayerRef()

  async function initializePlaylist(playlistItems?: PlaylistItem[]) {
    if (!playlistItems?.length) return;
    const _grouped = Object.values(groupBy(playlistItems, "playOrder"));

    let _frameIdx = 0;
    // @ts-ignore
    window.frameIdx = _frameIdx;
    do {
      const _framePool = _grouped[_frameIdx];
      const _randomIndex = Math.floor(Math.random() * _framePool.length);
      const _newPlaylistItem = _framePool[_randomIndex];

      if (isPlaylistItemActive(_newPlaylistItem)) {
        setCurrentFrame(_newPlaylistItem);

        if (_newPlaylistItem.type === "video") {
          await new Promise(async (resolve) => {
            onVideoEndedRef.current = () => resolve(undefined)
          });
          onVideoEndedRef.current = () => { }
        } else {
          await new Promise((resolve) =>
            setTimeout(resolve, _newPlaylistItem.duration * 1000),
          );
        }
      }

      if (_frameIdx === _grouped.length - 1) {
        _frameIdx = 0;
      } else {
        _frameIdx++;
      }
      // @ts-ignore
      window.frameIdx = _frameIdx;
      // eslint-disable-next-line no-constant-condition
    } while (true);
  }

  if (!fetchedPlaylist) return <>{loading}</>

  if (!currentFrame) return <>No Frames Active</>;

  function getTransitionVariant(_idx: number) {
    try {
      if (!fetchedPlaylist?.transition) return undefined;

      const _variants =
        typeof fetchedPlaylist.transition === "string"
          ? JSON.parse(fetchedPlaylist.transition)
          : fetchedPlaylist.transition;

      // @ts-expect-error - frameIdx might not be in window
      const _frameIdx = window.frameIdx;

      if (!_frameIdx) return undefined;

      const key = _frameIdx === 0 ? "0-1" : `${_frameIdx - 1}-${_frameIdx}`;

      // const key = "";
      if (key in _variants) {
        return _variants[key];
      }
      return undefined;
    } catch (_e) {
      return;
    }
  }

  function renderPlayer() {
    const item = fetchedPlaylist.items.find(
      (v) => v.id === currentFrame?.id,
    )
    if (!item) return;
    switch (currentFrame?.type) {
      case "asset":
        return <AssetPlayer asset={item} />;
      case "video":
        <VideoPlayer
          url={
            fetchedPlaylist?.items?.find(
              (v) => v.id === currentFrame.id,
            )?.url ?? ""
          }
        />
      case "layout":
      case "template":
        const layout = layouts.find((v) => v.id === currentFrame.id)
        if (!item) return
        return <LayoutPlayer
          key={`layout_${currentFrame.id}`}
          layoutInfo={layout}
        />
    }
  }

  return (
    <AnimatePresence initial={false}>
      <motion.div
        className="flex h-full w-full items-center justify-center overflow-clip"
        key={currentFrame.id}
        initial="initial"
        variants={getTransitionVariant(0)}
        animate="animate"
        exit="exit"
        transition={{
          x: { type: "spring", stiffness: 300, damping: 30 },
          opacity: { duration: 0.2 },
        }}
      >
        {renderPlayer()}
      </motion.div>
    </AnimatePresence>
  );
}
